import {
	Currency,
	type PlanNameType,
	type PlanProduct,
	type Price,
	type PaymentPeriodType,
} from '../../api/types/billing.interfaces'
import type {
	GetPricingRegionResponse,
	PricingPlanName,
	RegionalPlanProduct,
} from '../../api/types/pricing.interfaces'
import { getCurrencySymbol } from '../billing/formatter'

const PLAN_ORDER: PlanNameType[] = ['Free', 'Pro', 'Business', 'Enterprise']
const REGIONAL_PLAN_KEYS: Record<
	Exclude<PlanNameType, 'Free'>,
	PricingPlanName
> = {
	Pro: 'pro',
	Business: 'business',
	Enterprise: 'enterprise',
}

const parseCurrency = (value: unknown): Currency | undefined => {
	if (typeof value !== 'string') return undefined
	return Object.values(Currency).find(
		(currency) => currency === value.trim().toLowerCase(),
	)
}

const getCatalogAmount = (prices: Price[], period: PaymentPeriodType) => {
	const price = prices.find((item) => item.recurring.interval === period)
	return price?.unitAmount != null ? price.unitAmount / 100 : undefined
}

export const mapRegionalPlanProducts = (
	products: PlanProduct[],
	region?: GetPricingRegionResponse,
): RegionalPlanProduct[] => {
	if (!Array.isArray(products)) return []
	const regionalCurrency = parseCurrency(region?.currency)
	const result: RegionalPlanProduct[] = []

	for (const name of PLAN_ORDER) {
		const product = products.find((item) => item?.name === name)
		if (!product) continue
		const catalogPrices = (Array.isArray(product.prices) ? product.prices : []).filter(
			(price) =>
				typeof price?.id === 'string' &&
				price.id.trim().length > 0 &&
				parseCurrency(price.currency) != null &&
				Number.isSafeInteger(price.unitAmount) &&
				price.unitAmount >= 0 &&
				price.recurring?.interval_count === 1 &&
				(price.recurring.interval === 'month' || price.recurring.interval === 'year'),
		)

		const regionalAmounts =
			name === 'Free'
				? { monthly: 0, annual: 0 }
				: region?.plans?.[REGIONAL_PLAN_KEYS[name]]
		const amounts =
			regionalCurrency &&
			regionalAmounts &&
			Number.isFinite(regionalAmounts.monthly) && regionalAmounts.monthly >= 0 &&
			Number.isFinite(regionalAmounts.annual) && regionalAmounts.annual >= 0
				? regionalAmounts
				: undefined
		const fallbackPrice =
			catalogPrices.find((price) => parseCurrency(price.currency) === Currency.Usd)
			?? catalogPrices[0]
		const currency =
			(amounts ? regionalCurrency : parseCurrency(fallbackPrice?.currency))
			?? Currency.Usd

		const prices = catalogPrices
			.filter((price) => {
				if (parseCurrency(price.currency) !== currency) return false
				const interval = price.recurring.interval
				if (!amounts) return true
				const amount = interval === 'month' ? amounts.monthly : amounts.annual
				return price.unitAmount === Math.round(amount * 100)
			})
			.map((price) => ({ ...price, currency }))

		result.push({
			...product,
			prices,
			regionalPricing: {
				monthly: getCatalogAmount(prices, 'month'),
				annual: getCatalogAmount(prices, 'year'),
				...amounts,
				currency,
				symbol:
					(amounts && typeof region?.symbol === 'string' && region.symbol.trim())
					|| getCurrencySymbol(currency),
			},
		})
	}

	return result
}
